import { useState } from "react";
import { Plus, Trash2, ListChecks, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LangContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

export default function EventChecklist({ event, onChanged, canEdit, canDelete }) {
  const { t } = useLang();
  const [label, setLabel] = useState("");
  const items = event.checklist || [];
  const done = items.filter((c) => c.done).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;

  const add = async () => {
    if (!label.trim()) return;
    await api.post(`/events/${event.id}/checklist`, { label });
    setLabel(""); onChanged();
  };
  const seed = async () => { await api.post(`/events/${event.id}/checklist/default`); onChanged(); };
  const toggle = async (c) => { await api.patch(`/events/${event.id}/checklist/${c.id}`, { done: !c.done }); onChanged(); };
  const remove = async (c) => { await api.delete(`/events/${event.id}/checklist/${c.id}`); onChanged(); };

  return (
    <div data-testid="event-checklist">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5" />{t("checklist")} · {done}/{items.length}</div>
        <span data-testid="checklist-progress-label" className={`text-xs font-semibold ${pct === 100 && items.length ? "text-emerald-300" : "text-slate-300"}`}>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mb-3">
        <div data-testid="checklist-progress-bar" className={`h-full rounded-full transition-all ${pct === 100 && items.length ? "bg-emerald-400" : "bg-indigo-400/80"}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="space-y-1.5">
        {items.map((c) => (
          <div key={c.id} data-testid={`checklist-item-${c.id}`} className="flex items-center gap-2 glass rounded-lg px-3 py-2">
            <input type="checkbox" data-testid={`checklist-toggle-${c.id}`} checked={!!c.done} disabled={!canEdit} onChange={() => toggle(c)} className="w-4 h-4 accent-emerald-500 cursor-pointer" />
            <span className={`flex-1 text-sm ${c.done ? "line-through text-slate-500" : ""}`}>{c.label}</span>
            {c.done && c.done_by && <span className="text-[10px] text-slate-500">{c.done_by} · {c.done_at?.slice(0, 10)}</span>}
            {canDelete && <button data-testid={`checklist-delete-${c.id}`} onClick={() => remove(c)} className="text-slate-500 hover:text-rose-300"><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
        {items.length === 0 && canEdit && (
          <Button size="sm" variant="outline" data-testid="checklist-seed-btn" onClick={seed} className="border-white/10 bg-white/5"><Sparkles className="w-3.5 h-3.5 mr-1.5" />{t("checklist_default")}</Button>
        )}
        {items.length === 0 && !canEdit && <div className="text-xs text-slate-500">—</div>}
      </div>
      {canEdit && (
        <div className="flex gap-2 mt-2">
          <Input data-testid="checklist-new-input" placeholder={t("checklist_add")} value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} className="bg-white/5 border-white/10 h-9" />
          <Button size="sm" data-testid="checklist-add-btn" onClick={add} disabled={!label.trim()} className="bg-white/10 hover:bg-white/20 h-9"><Plus className="w-4 h-4" /></Button>
        </div>
      )}
    </div>
  );
}
